import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { IoSearchOutline } from "react-icons/io5";

const SearchGlyph = ({ className = "left-4", size = 18 }) => (
	<span className={`pointer-events-none absolute inset-y-0 flex items-center ${className}`}>
		<IoSearchOutline className="block text-gray-400" size={size} aria-hidden="true" />
	</span>
);

export const SheetSelect = memo(({ id, label, value, fieldKey, onChange, options, placeholder }) => (
	<div className="relative flex min-w-0 items-center px-1">
		<label htmlFor={id} className="sr-only">
			{label}
		</label>
		<select
			id={id}
			value={value || ""}
			onChange={(event) => onChange(fieldKey, event.target.value)}
			className={`h-12 w-full appearance-none bg-transparent pl-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-akgreen ${
				value ? "font-medium text-akgreenest" : "text-gray-800"
			}`}
		>
			<option value="">{placeholder}</option>
			{options.map((option) => (
				<option key={option.value} value={option.value}>
					{option.label}
				</option>
			))}
		</select>
		<span className="pointer-events-none absolute right-2 flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-gray-500">
			<FiChevronDown size={14} />
		</span>
	</div>
));

SheetSelect.displayName = "SheetSelect";

export const PillChoices = memo(
	({
		legend,
		items,
		selected,
		onToggle,
		searchLabel,
		emptyMessage,
		noMatchMessage,
		loadingMessage = "Loading…",
		remote = false,
		onSearchChange,
		hasMore = false,
		onLoadMore,
		isLoadingMore = false,
		totalCount,
	}) => {
		const scrollerRef = useRef(null);
		const scrollRafRef = useRef(null);
		const prevLengthRef = useRef(0);
		const [query, setQuery] = useState("");
		const [canScrollLeft, setCanScrollLeft] = useState(false);
		const [canScrollRight, setCanScrollRight] = useState(false);

		const visible = useMemo(() => {
			const known = new Set(items.map((item) => item.name));
			const selectedOnly = selected
				.filter((name) => name && !known.has(name))
				.map((name) => ({ id: `selected-${name}`, name }));
			const q = query.trim().toLowerCase();
			const matching =
				remote || !q ? items : items.filter((item) => item.name?.toLowerCase().includes(q));
			return [...selectedOnly, ...matching];
		}, [items, query, remote, selected]);

		const isNearEnd = useCallback((el) => {
			if (!el) return false;
			const lead = Math.max(el.clientWidth * 0.35, 80);
			return el.scrollLeft + el.clientWidth >= el.scrollWidth - lead;
		}, []);

		const maybeLoadMore = useCallback(() => {
			if (!hasMore || isLoadingMore) return;
			const el = scrollerRef.current;
			if (!el) return;
			if (isNearEnd(el) || el.scrollWidth <= el.clientWidth + 4) {
				onLoadMore?.();
			}
		}, [hasMore, isLoadingMore, isNearEnd, onLoadMore]);

		const updateScrollState = useCallback(() => {
			if (scrollRafRef.current) return;
			scrollRafRef.current = requestAnimationFrame(() => {
				scrollRafRef.current = null;
				const el = scrollerRef.current;
				if (!el) return;
				setCanScrollLeft(el.scrollLeft > 4);
				setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
				maybeLoadMore();
			});
		}, [maybeLoadMore]);

		useEffect(() => {
			const el = scrollerRef.current;
			if (!el) {
				setCanScrollLeft(false);
				setCanScrollRight(false);
				return undefined;
			}

			updateScrollState();

			const canObserve = typeof ResizeObserver !== "undefined";
			const observer = canObserve ? new ResizeObserver(updateScrollState) : null;
			if (observer) {
				observer.observe(el);
			} else {
				window.addEventListener("resize", updateScrollState);
			}

			return () => {
				if (observer) {
					observer.disconnect();
				} else {
					window.removeEventListener("resize", updateScrollState);
				}
				if (scrollRafRef.current) {
					cancelAnimationFrame(scrollRafRef.current);
					scrollRafRef.current = null;
				}
			};
		}, [visible.length, hasMore, isLoadingMore, updateScrollState]);

		useEffect(() => {
			const el = scrollerRef.current;
			const previous = prevLengthRef.current;
			const grew = items.length > previous;
			const reset = items.length === 0 || previous === 0 || items.length < previous;

			if (el && reset) {
				el.scrollLeft = 0;
				updateScrollState();
			} else if (el && grew && el.scrollLeft > 4 && isNearEnd(el)) {
				const distance = Math.max(el.clientWidth * 0.75, 180);
				el.scrollBy({ left: distance, behavior: "smooth" });
				updateScrollState();
			}

			prevLengthRef.current = items.length;
		}, [items.length, isNearEnd, updateScrollState]);

		useEffect(() => {
			const el = scrollerRef.current;
			if (!el) return;
			el.scrollLeft = 0;
			updateScrollState();
			// Intentionally only when the search text changes.
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [query]);

		const scrollByPage = (direction) => {
			const el = scrollerRef.current;
			if (!el) return;
			const distance = Math.max(el.clientWidth * 0.75, 180);

			if (direction > 0 && hasMore && !isLoadingMore && !canScrollRight) {
				onLoadMore?.();
				return;
			}

			if (direction > 0 && hasMore && !isLoadingMore && isNearEnd(el)) {
				onLoadMore?.();
			}

			el.scrollBy({ left: direction * distance, behavior: "smooth" });
		};

		const showRight = canScrollRight || hasMore;
		const hasQuery = Boolean(query.trim());
		const emptyText = (() => {
			if (isLoadingMore && visible.length === 0) return loadingMessage;
			if (hasQuery) return noMatchMessage;
			return emptyMessage;
		})();
		const totalLabel =
			typeof totalCount === "number" ? totalCount : hasQuery ? visible.length : items.length;
		const legendCount =
			selected.length > 0 ? `${selected.length} selected of ${totalLabel}` : `${totalLabel}`;

		return (
			<fieldset className="block w-full min-w-0" style={{ minInlineSize: 0 }}>
				<legend className="text-xs font-semibold uppercase tracking-wide text-gray-500">
					{legend}
					<span className="ml-2 font-normal normal-case tracking-normal text-gray-500">
						· {legendCount}
					</span>
				</legend>
				<div className="relative mt-2">
					<SearchGlyph className="left-3" size={15} />
					<input
						type="search"
						value={query}
						onChange={(event) => {
							setQuery(event.target.value);
							onSearchChange?.(event.target.value);
						}}
						onKeyDown={(event) => {
							if (event.key === "Enter") event.preventDefault();
						}}
						placeholder={searchLabel}
						aria-label={searchLabel}
						className="h-10 w-full rounded-full border border-transparent bg-gray-100 pl-9 pr-3 text-sm focus:border-akgreen focus:outline-none focus:ring-2 focus:ring-akgreen"
					/>
				</div>
				<div className="relative mt-3 w-full min-w-0 py-1">
					{visible.length === 0 && !isLoadingMore ? (
						<p className="text-sm text-gray-500">{emptyText}</p>
					) : (
						<>
							{canScrollLeft && (
								<div
									className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-white to-transparent"
									aria-hidden="true"
								/>
							)}
							{showRight && (
								<div
									className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent"
									aria-hidden="true"
								/>
							)}
							<div
								ref={scrollerRef}
								onScroll={updateScrollState}
								role="group"
								aria-label={legend}
								className="books-filter-row flex w-full min-w-0 max-w-full gap-2 overflow-x-auto overflow-y-hidden px-11"
							>
								{visible.map((item) => {
									const isSelected = selected.includes(item.name);
									return (
										<button
											key={item.id}
											type="button"
											aria-pressed={isSelected}
											onClick={() => onToggle(item.name)}
											className={`flex-shrink-0 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-akgreen focus:ring-offset-2 ${
												isSelected
													? "border-akgreenest bg-akgreenest text-white"
													: "border-gray-300 bg-white text-gray-800 hover:border-gray-400"
											}`}
										>
											{item.name}
										</button>
									);
								})}
								{isLoadingMore && (
									<span className="flex-shrink-0 whitespace-nowrap rounded-full border border-dashed border-gray-300 px-3 py-1.5 text-sm text-gray-500">
										Loading…
									</span>
								)}
							</div>
						</>
					)}
					{canScrollLeft && (
						<span className="absolute inset-y-0 left-0 z-10 flex items-center">
							<button
								type="button"
								onClick={() => scrollByPage(-1)}
								aria-label={`Show previous ${legend.toLowerCase()}`}
								className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-akgreenest shadow-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-akgreen"
							>
								<FiChevronLeft className="block" size={22} />
							</button>
						</span>
					)}
					{showRight && (visible.length > 0 || isLoadingMore) && (
						<span className="absolute inset-y-0 right-0 z-10 flex items-center">
							<button
								type="button"
								onClick={() => scrollByPage(1)}
								disabled={isLoadingMore && !canScrollRight}
								aria-label={`Show more ${legend.toLowerCase()}`}
								className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-akgreenest shadow-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-akgreen disabled:opacity-50"
							>
								<FiChevronRight className="block" size={22} />
							</button>
						</span>
					)}
				</div>
			</fieldset>
		);
	},
);

PillChoices.displayName = "PillChoices";
